import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ALL_CATERING, CATERING_CATEGORIES, OCCASIONS, CITIES } from '../data/smaken';
import { CateringCard } from '../components/CateringCard';
import { Reveal } from '../components/Reveal';
import { HowItWorks } from '../components/HowItWorks';
import { usePartnerSummaries, PartnerSelect, PartnerDetail } from '../components/CateringPartners';
import { Note, ArrowMark } from '../components/Marks';
import { useRequest } from '../CartContext';
const AREAS = Object.keys(CITIES);
export function Catering() {
  const [params, setParams] = useSearchParams();
  const {
    count
  } = useRequest();
  const category = params.get('kategori') ?? '';
  const occasion = params.get('tillfalle') ?? '';
  const area = params.get('ort') ?? '';
  const partner = params.get('verksamhet') ?? '';
  const partners = usePartnerSummaries();
  const activePartner = partners.find(p => p.slug === partner);
  const guests = params.get('gaster') ?? '';
  const query = params.get('q') ?? '';
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);else next.delete(key);
    setParams(next, {
      replace: true
    });
  };
  const results = useMemo(() => {
    const guestCount = Number(guests);
    const q = query.trim().toLowerCase();
    return ALL_CATERING.filter(offer => {
      if (category && offer.category !== category) return false;
      if (occasion && !offer.occasions.includes(occasion)) return false;
      if (area && offer.placeCity !== area) return false;
      if (partner && offer.placeSlug !== partner) return false;
      if (guestCount && offer.minGuests > guestCount) return false;
      if (!q) return true;
      return [offer.name, offer.description, offer.category, offer.placeName, ...offer.occasions].join(' ').toLowerCase().includes(q);
    });
  }, [category, occasion, area, partner, guests, query]);
  const chip = (active: boolean) => `border px-4 py-2 text-[11px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out ${active ? 'border-black bg-black text-papper' : 'border-black/20 hover:border-black'}`;
  return <div className="mx-auto max-w-page px-5 py-12 md:px-8 md:py-16" data-id="element-777" data-mp="1v0412w31jzet">
      <Reveal data-id="element-778" data-mp="1zu88thk4sbgy">
        <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-779" data-mp="1zuqdn2kuzy0h">
          <div data-id="element-780" data-mp="24qyoi3s0x51u">
            <h1 className="display-xl text-5xl uppercase sm:text-7xl" data-id="element-781" data-mp="24rgsxnouapku">Catering</h1>
            <Note className="mt-3 block" data-id="element-782" data-mp="29pi8vzqyy8s3">Vi vet vilka som kan leverera. Alla står vi för.</Note>
          </div>
          {count > 0 && <Link to="/forfragan" className="group flex items-center gap-2 bg-rod px-7 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-783" data-mp="2dqls8w886my2">
              Din förfrågan ({count})
              <ArrowMark className="h-3.5 w-8 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-784" data-mp="ofkz91bd8b11" />
            </Link>}
        </div>
      </Reveal>

      {/* Filters — sticky so choosing one never scrolls you away from the list */}
      <div className="z-30 -mx-5 mt-10 border-b border-black/12 bg-papper px-5 md:-mx-8 md:px-8 lg:sticky lg:top-[98px]" data-id="element-785" data-mp="757k8yz2q3tt">
        <div className="space-y-5 border-t-2 border-black py-6" data-id="element-786" data-mp="26wb6qglrxcn">
          <div data-id="element-787" data-mp="zt3g61gtjrpj">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-788" data-mp="zrim0cyf2as7">Tillfälle</p>
            <div className="flex flex-wrap gap-2" data-id="element-789" data-mp="uua0q3hz1qmq">
              <button onClick={() => setParam('tillfalle', '')} className={chip(!occasion)} data-id="element-790" data-mp="pudx45d6s17p">
                Hela dagen
              </button>
              {OCCASIONS.map(o => <button key={o} onClick={() => setParam('tillfalle', occasion === o ? '' : o)} className={chip(occasion === o)} data-id="element-791" data-mp="lvewgwyhmqm2">
                  {o}
                </button>)}
            </div>
          </div>

          <div data-id="element-792" data-mp="y8s174a6nsq1">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-793" data-mp="1311p6erqmipo">Typ</p>
            <div className="flex flex-wrap gap-2" data-id="element-794" data-mp="ocp2k8ejic7u">
              <button onClick={() => setParam('kategori', '')} className={chip(!category)} data-id="element-795" data-mp="oe01nux1ivsm">
                Allt
              </button>
              {CATERING_CATEGORIES.map(c => <button key={c} onClick={() => setParam('kategori', category === c ? '' : c)} className={chip(category === c)} data-id="element-796" data-mp="1h2e20hblq47c">
                  {c}
                </button>)}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-6 pt-1" data-id="element-797" data-mp="1c0oqz1odpqj9">
            <PartnerSelect partners={partners} selected={partner} onSelect={slug => setParam('verksamhet', slug)} data-id="element-798" data-mp="3sqvikc9sgp5y" />
            <div data-id="element-799" data-mp="khy0lynuyxc">
              <label htmlFor="c-guests" className="mb-2 block text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-800" data-mp="3p8fxvemju9au">
                Antal gäster
              </label>
              <input id="c-guests" type="number" min="1" value={guests} onChange={e => setParam('gaster', e.target.value)} placeholder="25" className="w-28 border-b border-black/25 bg-transparent pb-2 font-display text-2xl outline-none transition-colors duration-150 ease-out placeholder:text-black/25 focus:border-rod" data-id="element-801" data-mp="3kbpiktikjhqh" />
            </div>
            <div data-id="element-802" data-mp="3g8t67kjmghuz">
              <label htmlFor="c-area" className="mb-2 block text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-803" data-mp="3g9cfydz4bzxx">
                Levereras i
              </label>
              <select id="c-area" value={area} onChange={e => setParam('ort', e.target.value)} className="cursor-pointer border-b border-black/25 bg-transparent pb-2 font-display text-2xl outline-none transition-colors duration-150 ease-out focus:border-rod" data-id="element-804" data-mp="2nqp0be2w5d48">
                <option value="" data-id="element-805" data-mp="3cg094nmov41s">Hela Småland</option>
                {AREAS.map(a => <option key={a} data-id="element-806" data-mp="3hb656f9t1z3l">{a}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {activePartner && <PartnerDetail partner={activePartner} data-id="element-807" data-mp="2piypbh24iqq9" />}

      <p className="mt-8 text-[11px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-808" data-mp="2pk9thhzhe0hw">
        {results.length} alternativ
      </p>

      {results.length === 0 ? <div className="mt-6 border-y-2 border-black py-20 text-center" data-id="element-809" data-mp="2yh0nzpydx7ad">
          <p className="display-xl text-3xl uppercase" data-id="element-810" data-mp="33ipxv29vfewv">Inget matchar. Än.</p>
          <p className="mt-3 text-black/60" data-id="element-811" data-mp="dsw2iz89eydy">
            Justera antalet gäster eller släpp ett filter — vi har mer än det ser ut som.
          </p>
        </div> : <div className="mt-6 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-812" data-mp="itaegsczesh9">
          {results.map(offer => <CateringCard key={offer.id} offer={offer} data-id="element-813" data-mp="1yaop1u36k877" />)}
        </div>}

      <HowItWorks data-id="element-814" data-mp="1tjosipxz3s58" />

      {/* Always one tap from finishing the order, wherever you are in the list */}
      {count > 0 && <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-5 pb-5" data-id="element-815" data-mp="1oldiof7ohhn0">
          <Link to="/forfragan" className="group pointer-events-auto flex items-center gap-3 bg-rod px-7 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper shadow-lg shadow-black/20 transition-colors duration-150 ease-out hover:bg-black" data-id="element-816" data-mp="1kk1qc1xf6ib4">
            Gå vidare med {count} {count === 1 ? 'val' : 'val'}
            <ArrowMark className="h-3.5 w-8 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-817" data-mp="34yve669pjmr9" />
          </Link>
        </div>}
    </div>;
}